// CMS roles, held as Cognito groups and read from the ID token's
// cognito:groups claim. A user holds any combination; access is the union,
// and admin implies the other two. The API enforces the same roles in
// apps/api/shared/cognito_authentication.py — this side is for usability
// only (anyone can read the bundle). See docs/html/tennischannel/cms-rbac.html.

export const ROLES = ['live-ops', 'content', 'admin'] as const
export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  'live-ops': 'Live Ops',
  content: 'Content',
  admin: 'Admin',
}

const isRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).includes(value)

// Unknown groups are ignored: they grant nothing.
export const rolesFromGroups = (groups: unknown): Role[] =>
  Array.isArray(groups) ? groups.filter(isRole) : []

export const hasAnyRole = (held: readonly Role[], required: readonly Role[]): boolean =>
  held.includes('admin') || required.some((role) => held.includes(role))

// Route -> role, keyed by the first path segment. The API keeps its own map
// (required_roles on each router in apps/api/routes): its routers are shaped
// by entity, not by page, so the two maps have different keys on purpose.
// A segment missing here is admin-only until it is added.
const SECTION_ROLES: Record<string, readonly Role[]> = {
  live: ['live-ops'],
  matches: ['live-ops'],
  'vod-highlights': ['live-ops'],
  vods: ['live-ops'],
  series: ['live-ops'],
  tournaments: ['live-ops'],
  players: ['live-ops'],
  teams: ['live-ops'],
  'cup-teams': ['live-ops'],
  ties: ['live-ops'],
  courts: ['live-ops'],
  'feed-mapping': ['live-ops'],
  feeds: ['live-ops'],
  'match-briefing': ['live-ops'],
  'artwork-templates': ['live-ops'],
  content: ['content'],
  promotions: ['content'],
  'mvpd-providers': ['admin'],
  users: ['admin'],
}

const ADMIN_ONLY: readonly Role[] = ['admin']

// undefined only for the index route, which redirects by role.
export const rolesForPath = (pathname: string): readonly Role[] | undefined => {
  const segment = pathname.split('/')[1] ?? ''
  if (!segment) return undefined
  // Own keys only, so /constructor is not Object.prototype's function.
  return Object.prototype.hasOwnProperty.call(SECTION_ROLES, segment) ? SECTION_ROLES[segment] : ADMIN_ONLY
}

export const canOpen = (held: readonly Role[], pathname: string): boolean => {
  const required = rolesForPath(pathname)
  return !required || hasAnyRole(held, required)
}

// Where the index route sends each person. Every role opens one of the two;
// a person with no role never reaches the routes (see RootLayout).
export const landingPath = (held: readonly Role[]): string =>
  canOpen(held, '/matches') ? '/matches' : '/content'
